import {Pipe, PipeTransform} from '@angular/core';

/**
 * Renders a small markdown subset (headings, bold, italic, inline code, lists, paragraphs)
 * as html. Input is html-escaped first, so the result only contains tags generated by this
 * pipe and stays compatible with angular's default innerHTML sanitization.
 */
@Pipe({name: 'markdown'})
export class MarkdownPipe implements PipeTransform {
  public transform(value: string): string {
    if (!value) {
      return '';
    }

    const lines = this.escapeHtml(value).split(/\r?\n/);
    const html: string[] = [];
    let listTag: string = null;
    let paragraph: string[] = [];

    const closeList = () => {
      if (listTag) {
        html.push(`</${listTag}>`);
        listTag = null;
      }
    };

    const flushParagraph = () => {
      if (paragraph.length) {
        html.push(`<p>${paragraph.join('<br>')}</p>`);
        paragraph = [];
      }
    };

    for (const line of lines) {
      const trimmed = line.trim();

      if (!trimmed) {
        flushParagraph();
        closeList();
        continue;
      }

      const heading = trimmed.match(/^(#{1,6})\s+(.*)$/);
      if (heading) {
        flushParagraph();
        closeList();
        const level = Math.min(heading[1].length + 3, 6);
        html.push(`<h${level}>${this.inline(heading[2])}</h${level}>`);
        continue;
      }

      const unordered = trimmed.match(/^[-*+]\s+(.*)$/);
      if (unordered) {
        flushParagraph();
        if (listTag !== 'ul') {
          closeList();
          html.push('<ul>');
          listTag = 'ul';
        }
        html.push(`<li>${this.inline(unordered[1])}</li>`);
        continue;
      }

      const ordered = trimmed.match(/^\d+[.)]\s+(.*)$/);
      if (ordered) {
        flushParagraph();
        if (listTag !== 'ol') {
          closeList();
          html.push('<ol>');
          listTag = 'ol';
        }
        html.push(`<li>${this.inline(ordered[1])}</li>`);
        continue;
      }

      closeList();
      paragraph.push(this.inline(trimmed));
    }

    flushParagraph();
    closeList();

    return html.join('');
  }

  /**
   * Renders inline markdown: bold, italic and inline code
   */
  private inline(text: string): string {
    return text
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/__([^_]+)__/g, '<strong>$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>');
  }

  /**
   * Escapes html special chars
   */
  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
